import React, { FC } from 'react';
import './PaymentBrandingComponent.css';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { colors } from '../../colors/Colors';

import visaLogo from '../../assets/cards/visa.gif';
import mastercardLogo from '../../assets/cards/mastercard.png';
import maestroLogo from '../../assets/cards/maestro.png';
import amexLogo from '../../assets/cards/american-express.png';
import dinacardLogo from '../../assets/payment/dinacard.png';
import bankIntesaLogo from '../../assets/payment/bank-intesa.png';
import monriLogo from '../../assets/payment/monri-payten.png';
import visaSecureLogo from '../../assets/payment/visa-secure.png';
import mastercardIdCheckLogo from '../../assets/payment/mastercard-id-check.png';
import dinacardSecureLogo from '../../assets/payment/dinacard-secure.png';
import amexSafeKeyLogo from '../../assets/payment/amex-safekey.png';

/**
 * Spoljni linkovi propisani Monri-jevim dokumentom "Zakonski obavezni podaci,
 * izjave i smernice za uređivanje ovlašćenih internet (e-Commerce) prodajnih
 * mesta u Srbiji" (tačka 3 - Brendiranje):
 *   - Logoe banke, Monri-a i sigurnosnih protokola OBAVEZNO linkujemo.
 *   - Logoe platnih instrumenata (VISA, MC, Maestro, DinaCard, AmEx) NIKAKO ne linkujemo.
 */
// URL-ovi preuzeti iz Monri-jevog PDF-a "Zakonski obavezni podaci, izjave i smernice...",
// tacka 3 - Brendiranje (klikabilni hyperlink-ovi u PDF-u).
// VAZNO: NE menjati bez Monri saglasnosti — testirano sa njihovim PaymentGateway timom.
const LINK_VISA_SECURE =
    'https://rs.visa.com/pay-with-visa/security-and-assistance/protected-everywhere.html';
const LINK_MASTERCARD_ID_CHECK =
    'https://www.mastercard.com/rs/sr/personal/find-a-card.html';
const LINK_MONRI = 'https://monri.rs/';
const LINK_INTESA = 'https://www.bancaintesa.rs/';

export type PaymentBrandingTheme = 'on-dark' | 'on-light';

interface PaymentBrandingProps {
    /**
     * `on-dark`  – footer / tamna pozadina: logoi dobijaju beli "chip" iza sebe radi čitljivosti.
     * `on-light` – payment forme na beloj pozadini: logoi su providni, bez chip-a.
     */
    theme?: PaymentBrandingTheme;
    /** Prikazuje "Sigurno plaćanje" naslov iznad logotipa (footer da, forme ne). */
    showTitle?: boolean;
    /**
     * Prikazuje grupu sa logotipima kartica (VISA, MasterCard, Maestro, DinaCard,
     * AmEx). Default `true`. Footer postavlja na `false` dok Monri kartica nije
     * globalno omogucen (banka jos nije odobrila), jer bi prikazivanje logoa
     * kartica koje vecina posetilaca ne moze da koristi bilo zbunjujuce.
     */
    showCards?: boolean;
}

/**
 * Brendiranje plaćanja prikazano svuda gde se vrši ili promoviše kartično plaćanje
 * (footer, Step-4 kod sesija, Workshop Step-2). Raspored je striktno propisan Monri-jem:
 *
 *   [karte: VISA | MC | Maestro | DinaCard | AmEx]
 *      |  [banka Intesa | Monri Payments]                    ← razdvajač
 *           |  [VISA Secure | MC ID Check | DinaCard Secure | AmEx SafeKey]
 *
 * Linkovi obavezno otvaraju novi tab (target="_blank") sa noopener/noreferrer.
 */
export const PaymentBrandingComponent: FC<PaymentBrandingProps> = ({
    theme = 'on-light',
    showTitle = false,
    showCards = true,
}) => {
    const { t } = useTranslation();

    return (
        <div className={`payment-branding payment-branding--${theme}`}>
            {showTitle && (
                <Typography
                    variant="h6"
                    color={theme === 'on-dark' ? colors.background : colors.primary}
                    sx={{ textAlign: 'center', marginBottom: '0.75rem' }}
                >
                    {t('footer.payment.title')}
                </Typography>
            )}
            <div className="payment-branding-row">
                {showCards && (
                    <div className="payment-branding-group payment-branding-group--cards">
                        <img src={visaLogo} alt="VISA" />
                        <img src={mastercardLogo} alt="MasterCard" />
                        <img src={maestroLogo} alt="Maestro" />
                        <img src={dinacardLogo} alt="DinaCard" />
                        <img src={amexLogo} alt="American Express" />
                    </div>
                )}
                 {showCards && (<div className="payment-branding-group payment-branding-group--brand">
                    <a
                        href={LINK_INTESA}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Banca Intesa"
                    >
                        <img src={bankIntesaLogo} alt="Banca Intesa" />
                    </a>
                    <a
                        href={LINK_MONRI}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Monri Payments"
                    >
                        <img src={monriLogo} alt="Monri Payments" />
                    </a>
                </div>
                )}
                {showCards && (<div className="payment-branding-group payment-branding-group--secure">
                    <a
                        href={LINK_VISA_SECURE}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="VISA Secure"
                    >
                        <img src={visaSecureLogo} alt="VISA Secure" />
                    </a>
                    <a
                        href={LINK_MASTERCARD_ID_CHECK}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Mastercard ID Check"
                    >
                        <img src={mastercardIdCheckLogo} alt="Mastercard ID Check" />
                    </a>
                    <img src={dinacardSecureLogo} alt="DinaCard Secure" />
                    <img src={amexSafeKeyLogo} alt="American Express SafeKey" />
                </div>
                )}
                
            </div>
        </div>
    );
};
